<template>
  <page-main title="Job Fit Analyzer" noBottom >
    <template v-slot:aside>
      <div>
        <p>
          Have a job description for a role you're eyeing? Use this tool to
          evaluate whether your resume is a good fit and get customized
          feedback on how to make it stronger.
        </p>
        <ul>
          <li>
            Select one of your saved resumes from the dropdown, enter the
            company name and job title, then paste the full job description
            (up to 8,000 characters) and submit for a customized response.
          </li>
          <li>
            Be sure to download your report if you want to keep it. Job fit
            analyses are not saved by Resumation.
          </li>
        </ul>
        <div class="aside-chips">
          <span class="aside-chip">★ Plus only</span>
        </div>
      </div>
    </template>
    <template v-slot:nav>
      <modal cardTitle="Tips" ctaText="Need a few tips?" :accentButton="true">
        <ul>
          <li>
            <span class="font-weight-bold">Tip #1:</span> Search online job
            board sites for roles you like and/or fit your professional profile.
            Popular sites include LinkedIn, Indeed, Google, and Monster (however
            there are many more to search).
          </li>
          <li>
            <span class="font-weight-bold">Tip #2:</span> Copy and paste the
            entire job description into the text field. Make sure you capture
            the job title, description, qualifications, and company name - don't
            worry about formatting, we'll take care of that!
          </li>
          <li>
            <span class="font-weight-bold">Tip #3:</span> Don't know where to
            start? Use the Resumation Career Pathway Analyzer for suggestions on
            what jobs or roles might best suit you!
          </li>
        </ul>
      </modal>
      <v-btn outlined class="mt-4" block @click="navToContactUs">
        Any feedback? Contact us!
      </v-btn>
    </template>
    <v-container fluid class="container-main">
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="selectedResume"
            :items="resumeOptions"
            item-text="name"
            item-value="id"
            label="Select saved resume or full profile"
            tabindex="1"
            @change="resumeChange"
            autofocus
          ></v-select>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="companyName"
            label="Company Name"
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field v-model="jobTitle" label="Job Title"></v-text-field>
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="jobDescription"
            label="Copy and paste the full job description text below"
            rows="10"
            :counter="8000"
          ></v-textarea>
        </v-col>
        <v-col
          cols="12"
          class="text-center d-flex justify-space-between align-center"
        >
          <v-btn
            :disabled="
              !selectedResume ||
                !companyName ||
                !jobTitle ||
                !jobDescription ||
                jobDescription.length > 8000 ||
                isAnalyzing
            "
            :class="{ 'grey-btn': !selectedResume }"
            @click="optimizeWithAI"
            tabindex="3"
          >
            Submit
          </v-btn>
        </v-col>
        <v-col cols="12" style=" padding-bottom: 48px;">
          <v-card
            v-if="isAnalyzing || aiOptimizedPhrasing || showAiOptimizationError"
            class="pa-3"
          >
            <v-row>
              <v-col cols="12" class="d-flex justify-space-between">
                <span><b>Job Fit Analysis</b></span>
                <v-btn
                  v-if="
                    !isAnalyzing &&
                      aiOptimizedPhrasing &&
                      !showAiOptimizationError
                  "
                  small
                  @click="downloadReport"
                  >CLICK TO DOWNLOAD ANALYSIS
                </v-btn>
              </v-col>
              <v-col cols="12">
                <div v-if="isAnalyzing" class="text-center py-6">
                  <v-progress-circular indeterminate color="primary" size="48" class="mb-3"></v-progress-circular>
                  <p class="text-body-1 font-weight-medium mb-1">Analyzing...</p>
                  <p class="text-body-2 font-italic text--secondary mb-0">
                    This may take a minute to complete
                  </p>
                </div>
                <div v-else-if="showAiOptimizationError">
                  <v-card-text
                    class="text--primary text-body-1 pt-4 pb-3 text-center"
                  >
                    <h3 class="pt-2">Oops. Something went wrong.</h3>
                    <h3 class="text-body-1 font-italic text--secondary">
                      {{ errorMessage }}
                    </h3>
                  </v-card-text>
                  <v-card-actions
                    :class="
                      $vuetify.breakpoint.xsOnly
                        ? 'flex-wrap justify-center'
                        : 'flex-wrap justify-end'
                    "
                  >
                  </v-card-actions>
                </div>
                <div v-else>
                  <p>Generated: {{ new Date().toLocaleDateString() }}</p>
                  <p>Based on: {{ selectedResumeName }}</p>
                  <v-alert type="info" outlined dense class="mb-4 text-body-2">
                    Please note that this assessment is based on the information provided in the job description and resume. It is not professional advice, and the accuracy of the assessment is subjective and may be incorrect. Users should use this assessment with caution and seek professional advice.
                  </v-alert>
                  <v-card v-if="profileSummaryText" outlined class="mb-3">
                    <v-card-text class="text--primary text-body-2 pt-3">
                      <span v-html="formatLine(profileSummaryText)"></span>
                    </v-card-text>
                  </v-card>
                  <v-card
                    v-for="(section, i) in filteredSections"
                    :key="i"
                    outlined
                    class="mb-3"
                  >
                    <v-card-title v-if="section.header" class="text-subtitle-1 font-weight-bold py-2" style="word-break: break-word;">
                      {{ section.header }}
                    </v-card-title>
                    <v-divider v-if="section.header"></v-divider>
                    <v-card-text class="text--primary text-body-2 pt-3">
                      <div
                        v-for="(line, j) in section.lines"
                        :key="j"
                        :class="{ 'mb-2': line !== '' }"
                      >
                        <span v-html="formatLine(line)"></span>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          class="text-center"
          v-if="aiOptimizedPhrasing && !isAnalyzing && !showAiOptimizationError"
        >
          <v-btn @click="resetDialog = true">Reset</v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="resetDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline"
          >Are you sure you want to reset this page?</v-card-title
        >
        <v-card-text
          >You will lose the analysis you generated. Make sure you download your
          analysis if you want to reference it later.</v-card-text
        >
        <v-card-actions>
          <v-btn color="blue darken-1" text @click="resetResumeState"
            >No - Return to Analysis</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="resetCareerReport"
            >Yes - Reset</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- PR-UPGRADE-MODAL: the ad-hoc premium r-dialog was replaced by the one
         shared UpgradeToPlusModal (mounted globally in App.vue). The gating
         logic below is unchanged; it now opens the shared modal via
         `upgradeModal/open` so the upgrade prompt looks identical everywhere. -->
  </page-main>
</template>

<script>
import * as log from "../../utility/logging/logger";
import * as resApi from "../../api/ResumeApi";
import { GetProfileContext } from "../../api/ProfileContextApi";
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import CareerReportBuilder from "../../docxBuilder/careerReportBuilder.js";
import { saveAs } from "file-saver";

export default {
  data() {
    return {
      context: "job-fit-analyzer",
      selectedResume: "",
      previousSelectedResume: "",
      companyName: "",
      jobTitle: "",
      jobDescription: "",
      aiOptimizedPhrasing: "",
      aiOptimizedDisplay: "",
      aiOptimizationStatus: "idle",
      errorMessage: "",
      isAnalyzing: false,
      resetDialog: false,
      resetResume: true,
      maxLengthRule: [(v) => v.length <= 8000 || "Max 8000 characters allowed"],
    };
  },
  methods: {
    // PR-UPGRADE-MODAL: single helper that opens the shared upgrade prompt
    // for this tool. Replaces the local r-dialog + navToPremiumPage redirect.
    // Gating logic (when this is called) is unchanged.
    openUpgrade() {
      this.dialog = false;
      this.$store.dispatch("upgradeModal/open", {
        featureName: "the Job Fit Analyzer",
        message:
          "Job Fit scoring runs on the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
    },
    ...mapActions("auth", ["refreshPremium"]),
    ...mapActions("userSubmissions", ["loadResumeSubmissions"]),
    resetCareerReport() {
      this.aiOptimizationStatus = "idle";
      this.resetDialog = false;
      this.isAnalyzing = false;
      this.aiOptimizedPhrasing = "";
      if (this.resetResume) {
        this.selectedResume = "";
        this.jobDescription = "";
        this.jobTitle = "";
        this.companyName = "";
      }
      this.resetResume = true;
    },
    resetResumeState() {
      this.selectedResume = this.previousSelectedResume;
      this.resetDialog = false;
      this.resetResume = true;
    },
    resumeChange() {
      if (this.aiOptimizedPhrasing !== "" || this.isAnalyzing == true) {
        this.resetDialog = true;
        this.resetResume = false;
      } else {
        this.previousSelectedResume = this.selectedResume;
      }
    },
    async optimizeWithAI() {
      this.isAnalyzing = true;
      try {
        log.logInformation("Refreshing user premium and attempts status...");
        await this.refreshPremium();

        if (!this.isPremium) {
          this.openUpgrade();
          this.isAnalyzing = false;
        } else {
          let reportData = await this.buildReportDataString();
          if (reportData == "Error") {
            return null;
          }
          let result = await resApi.FetchAiOptimizedPhrasing({
            resumeId: this.$store.state.resumeId,
            phrasing: reportData,
            context: this.context,
          });
          if (!result.success) {
            log.logError(
              "Failed to get AI optimized description",
              "optimizeWithAI"
            );
            this.aiOptimizationStatus = "error";
            this.errorMessage = "Failed to get AI Response";
            if (result.displayText == "Max Attempts Reached") {
              this.errorMessage =
                "Failed to get AI Response: Max usage reached.";
            }
          } else {
            if (this.isAnalyzing == true) {
              this.aiOptimizedPhrasing = result.data.optimizedPhrasing;
              this.buildReportDisplay();
            }
          }
          await this.refreshPremium();
          this.isAnalyzing = false;
        }
      } catch (e) {
        log.logError("Failed to get AI optimized description", e);
        this.aiOptimizationStatus = "error";
      }
    },
    async buildReportDataString() {
      if (this.selectedResume === "__full_profile__") {
        return await this.buildProfileDataString();
      }
      const resume = this.resumes.find((r) => r.id === this.selectedResume);
      if (!resume) {
        return "Error";
      }
      const fileRef = resume.fileRef;
      const response = await fetch(fileRef);
      const arrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });
      const resumeText = doc.getFullText();
      let result = resumeText.concat(
        " Company Name: " +
          this.companyName +
          " Job Title: " +
          this.jobTitle +
          " Job Description: " +
          this.jobDescription
      );
      return result;
    },
    async buildProfileDataString() {
      try {
        const result = await GetProfileContext(this.$store.state.resumeId);
        if (!result.success || !result.data) return "Error";
        const d = result.data;
        let text = "";
        const name = [d.name?.firstName, d.name?.lastName].filter(Boolean).join(" ");
        if (name) text += "Name: " + name + "\n";
        if (d.professionalSummary) text += "Professional Summary: " + d.professionalSummary + "\n";
        if (d.skills?.hard?.length) text += "Hard Skills: " + d.skills.hard.join(", ") + "\n";
        if (d.skills?.soft?.length) text += "Soft Skills: " + d.skills.soft.join(", ") + "\n";
        if (d.skills?.technical?.length) text += "Technical Skills: " + d.skills.technical.join(", ") + "\n";
        if (d.workExperience?.length) {
          text += "Work Experience:\n";
          for (const job of d.workExperience) {
            const title = job.positions?.[0]?.title || "";
            text += `- ${title} at ${job.company || ""} (${job.startDate || ""} to ${job.isCurrent ? "Present" : job.endDate || ""})\n`;
            if (job.achievements?.length) {
              for (const ach of job.achievements) {
                text += `  Achievement: ${ach.description || ""}\n`;
              }
            }
          }
        }
        if (d.education?.length) {
          text += "Education:\n";
          for (const edu of d.education) {
            text += `- ${edu.degree || ""} ${edu.fieldOfStudy ? "in " + edu.fieldOfStudy : ""} from ${edu.school || ""}\n`;
          }
        }
        if (d.credentials?.length) {
          text += "Professional Credentials:\n";
          for (const cred of d.credentials) {
            let credLine = `- ${cred.name || ""}`;
            if (cred.jurisdiction) credLine += ` (${cred.jurisdiction})`;
            if (cred.status) credLine += ` [${cred.status}]`;
            if (cred.issuingAuthority) credLine += ` issued by ${cred.issuingAuthority}`;
            text += credLine + "\n";
          }
        }
        if (d.certifications?.length) text += "Certifications: " + d.certifications.join(", ") + "\n";
        if (d.smallWins?.length) {
          text += "Recent Achievements:\n";
          for (const win of d.smallWins.slice(0, 10)) {
            text += `- ${win.description || ""}\n`;
            if (win.context) text += `  Context: ${win.context}\n`;
            if (win.action) text += `  Action: ${win.action}\n`;
            if (win.outcome) text += `  Outcome: ${win.outcome}\n`;
          }
        }
        text += " Company Name: " + this.companyName + " Job Title: " + this.jobTitle + " Job Description: " + this.jobDescription;
        return text;
      } catch (e) {
        log.logError("Failed to build profile data string", e);
        return "Error";
      }
    },
    buildReportDisplay() {
      const text = this.aiOptimizedPhrasing;
      const lines = text.split("\n");
      const sections = [];
      let current = { header: "", lines: [] };
      for (let line of lines) {
        const trimmed = line.trim();
        if (this.isSectionHeader(trimmed)) {
          if (current.header || current.lines.length > 0) {
            sections.push(current);
          }
          let header = trimmed;
          header = header.replace(/^##\s*/, "");
          header = header.replace(/^###\s*/, "");
          header = header.replace(/^\*\*(.*?)\*\*:?$/, "$1");
          header = header.replace(/:$/, "");
          current = { header: header, lines: [] };
        } else {
          current.lines.push(trimmed);
        }
      }
      if (current.header || current.lines.length > 0) {
        sections.push(current);
      }
      this.aiOptimizedDisplay = sections;
    },
    isSectionHeader(line) {
      if (!line || line.length === 0) return false;
      if (line.startsWith("## ") || line.startsWith("### ")) return true;
      if (/^\*\*.+\*\*:?\s*$/.test(line)) return true;
      if (line.length > 2 && line === line.toUpperCase() && /[A-Z]/.test(line) && !/^\d/.test(line)) return true;
      if (/^[A-Z][A-Za-z\s&,/()-]+:\s*$/.test(line) && line.length < 80) return true;
      return false;
    },
    formatLine(line) {
      if (!line) return "";
      let escaped = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      escaped = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      escaped = escaped.replace(/(?:^|\s)-\s+\*([^*]+)\*/g, " - <em>$1</em>");
      return escaped;
    },
    async downloadReport() {
      try {
        const builder = new CareerReportBuilder();
        builder.CreateText(this.aiOptimizedPhrasing, this.selectedResumeName);
        const blob = await builder.BuildDocx("Job Fit Analysis");
        saveAs(blob, "JobFitAnalysis.docx");
      } catch (e) {
        log.logError("Failed to build downloaded docx", e);
        this.aiOptimizationStatus = "error";
        this.errorMessage = "Error Building downloadable docx.";
      }
    },
    async loadData() {
      try {
        await this.refreshPremium();
        await this.loadResumeSubmissions();
      } catch (e) {
        log.logError("Failed to load resumes", e);
      }
    },
    navToContactUs() {
      window.open("https://www.resumation.net/contact", "_blank");
    },
  },
  computed: {
    resumeOptions() {
      const options = [{ id: "__full_profile__", name: "Full Resumation Profile (all your data)" }];
      if (this.resumes && this.resumes.length) {
        options.push(...this.resumes);
      }
      return options;
    },
    selectedResumeName() {
      if (this.previousSelectedResume === "__full_profile__") return "Full Resumation Profile";
      const resume = this.resumes.find(
        (r) => r.id === this.previousSelectedResume
      );
      return resume ? resume.name : "No resume selected";
    },
    parsedSections() {
      if (!this.aiOptimizedDisplay || !Array.isArray(this.aiOptimizedDisplay)) return [];
      return this.aiOptimizedDisplay;
    },
    profileSummaryText() {
      var sections = this.parsedSections;
      if (!sections || sections.length === 0) return "";
      var first = sections[0];
      if (first.header) return "";
      var disclaimerPattern = /please note that this assessment/i;
      var lines = first.lines || [];
      var summaryLines = [];
      for (var i = 0; i < lines.length; i++) {
        if (disclaimerPattern.test(lines[i])) continue;
        if (lines[i].trim()) summaryLines.push(lines[i]);
      }
      return summaryLines.join(" ").trim();
    },
    filteredSections() {
      var sections = this.parsedSections;
      if (!sections || sections.length === 0) return [];
      var result = [];
      var disclaimerPattern = /please note that this assessment/i;
      for (var i = 0; i < sections.length; i++) {
        var section = sections[i];
        if (!section.header) {
          var filteredLines = [];
          for (var j = 0; j < (section.lines || []).length; j++) {
            if (!disclaimerPattern.test(section.lines[j]) && section.lines[j].trim()) {
              filteredLines.push(section.lines[j]);
            }
          }
          if (filteredLines.length > 0 && this.profileSummaryText) continue;
          if (filteredLines.length > 0) {
            result.push({ header: section.header, lines: filteredLines });
          }
        } else {
          result.push(section);
        }
      }
      return result;
    },
    showAiOptimizationError() {
      return this.aiOptimizationStatus === "error";
    },
    ...mapFields("auth", {
      isPremium: "isPremium",
      remainingAiOptimizerAttempts: "remainingAiOptimizerAttempts",
      maxJobFitAttempts: "maxJobFitAttempts",
      jobFitAttempts: "jobFitAttempts",
    }),
    ...mapFields("userSubmissions", {
      resumes: "resumes",
    }),
  },
  async mounted() {
    await this.loadData();
    if (!this.isPremium) {
      this.openUpgrade();
    }
  },
};
</script>
